import * as pc from "playcanvas";
import { getWeapon } from "@hupper/shared/weapons";
import type { GameClient } from "../net/GameClient";
import type { AABB } from "../scene/Arena";

function createSparkTexture(device: pc.GraphicsDevice): pc.Texture {
  const size = 16;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (ctx) {
    const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    gradient.addColorStop(0, "rgba(255, 255, 255, 1)");
    gradient.addColorStop(0.2, "rgba(255, 200, 100, 0.8)");
    gradient.addColorStop(1, "rgba(255, 100, 0, 0)");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);
  }
  const texture = new pc.Texture(device, {
    width: size,
    height: size,
    format: pc.PIXELFORMAT_R8_G8_B8_A8,
    mipmaps: false,
  });
  texture.setSource(canvas);
  return texture;
}

function createBulletHoleTexture(device: pc.GraphicsDevice): pc.Texture {
  const size = 64;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (ctx) {
    // Outer dark ring/cracks
    ctx.fillStyle = "rgba(20, 20, 20, 0.6)";
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size * 0.4, 0, Math.PI * 2);
    ctx.fill();
    // Inner deep hole
    ctx.fillStyle = "rgba(5, 5, 5, 0.95)";
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size * 0.2, 0, Math.PI * 2);
    ctx.fill();
  }
  const texture = new pc.Texture(device, {
    width: size,
    height: size,
    format: pc.PIXELFORMAT_R8_G8_B8_A8,
    mipmaps: true,
  });
  texture.setSource(canvas);
  return texture;
}

/**
 * Tracer + WallImpact — Sprint 5.
 *
 * Listens on every server-broadcast `weapon_fire`. For each peer shot:
 *   1. Locally raycasts the ray vs world AABBs to determine the far end
 *      (clamped at 60 m for misses, the impact point otherwise).
 *   2. Spawns a glowing additive cylinder along the ray (peer tracer)
 *      with weapon-specific length/color. CS:GO convention: the
 *      shooter does NOT see their own tracer.
 *   3. Spawns a spark particle burst + textured decal at the wall impact (visible
 *      to everyone including the shooter — that's the "where my shot
 *      landed" feedback).
 *
 * Player capsules are deliberately skipped during the raycast — when a
 * shot hits a player, BloodSplatter renders at the body and the tracer
 * just continues to its programmed length (peers can read the hit from
 * the body splatter).
 */
export class Tracer {
  private readonly app: pc.AppBase;
  private readonly client: GameClient;
  private readonly collisionBoxes: AABB[];
  private readonly unsubs: Array<() => void> = [];
  private sparkTexture: pc.Texture | null = null;
  private bulletHoleTexture: pc.Texture | null = null;
  
  /**
   * Optional resolver for the shooter's hand/weapon world position. When
   * present and it returns a position for the attacker, the peer tracer
   * starts at the gun instead of the eye. Falls back to the eye-relative
   * heuristic below when absent or the shooter is unknown (e.g. just
   * despawned).
   */
  private readonly handResolver?: (sessionId: string, out: pc.Vec3) => boolean;
  /** Scratch vector for the resolver, reused per shot (no per-fire alloc). */
  private readonly scratchHand = new pc.Vec3();

  /**
   * Max tracer/impact range when nothing is hit (m). Must comfortably
   * exceed the arena diagonal so every shot fired from inside the map
   * reaches at least the far perimeter wall. Sprint 2 arena is 80×50×12,
   * diagonal ≈ 95 m. With 60 m, long shots (e.g. spawn-to-spawn ≈ 70 m)
   * fell short of any AABB → `boxIndex = -1` and the wall-impact dust
   * never spawned on the far walls. 120 m covers the full play volume
   * plus a margin for steep-angle shots.
   */
  private static readonly MAX_RANGE = 120;

  constructor(
    app: pc.AppBase,
    client: GameClient,
    collisionBoxes: AABB[],
    handResolver?: (sessionId: string, out: pc.Vec3) => boolean,
  ) {
    this.app = app;
    this.client = client;
    this.collisionBoxes = collisionBoxes;
    this.handResolver = handResolver;

    if (this.app.graphicsDevice) {
      this.sparkTexture = createSparkTexture(this.app.graphicsDevice);
      this.bulletHoleTexture = createBulletHoleTexture(this.app.graphicsDevice);
    }

    this.unsubs.push(
      this.client.onPeerFire((msg) => {
        // Resolve the impact point against world AABBs. The server-trusted
        // ray starts at the shooter's eye (msg.ox/oy/oz) and uses msg.dx/dy/dz
        // as the aim direction — those drive both the hit math and the
        // impact location. We DO NOT touch them: changing the ray would
        // alter what the server agrees was hit.
        const hit = this.rayWorldHit(msg.ox, msg.oy, msg.oz, msg.dx, msg.dy, msg.dz);
        const endX = msg.ox + msg.dx * hit.t;
        const endY = msg.oy + msg.dy * hit.t;
        const endZ = msg.oz + msg.dz * hit.t;

        // Peer tracer — skip if local player is the shooter (CS:GO style).
        if (msg.attackerSessionId !== this.client.sessionId) {
          let sx: number, sy: number, sz: number;
          if (this.handResolver?.(msg.attackerSessionId, this.scratchHand)) {
            // Preferred: start at the shooter's actual rendered hand/weapon,
            // nudged forward along the aim to the muzzle. This is what makes
            // the shot read as leaving the gun, not the head. End point stays
            // at the real impact — nothing here changes hit detection or aim.
            sx = this.scratchHand.x + msg.dx * Tracer.MUZZLE_FORWARD;
            sy = this.scratchHand.y + msg.dy * Tracer.MUZZLE_FORWARD;
            sz = this.scratchHand.z + msg.dz * Tracer.MUZZLE_FORWARD;
          } else {
            // Fallback heuristic (shooter unknown / despawned): shift the
            // origin from the eye down + forward + slightly right so it still
            // exits near the gun rather than the head.
            const horizLen = Math.sqrt(msg.dx * msg.dx + msg.dz * msg.dz) || 1;
            const rightX = msg.dz / horizLen;
            const rightZ = -msg.dx / horizLen;
            sx = msg.ox + msg.dx * Tracer.HAND_FORWARD + rightX * Tracer.HAND_RIGHT;
            sy = msg.oy + msg.dy * Tracer.HAND_FORWARD - Tracer.HAND_DOWN;
            sz = msg.oz + msg.dz * Tracer.HAND_FORWARD + rightZ * Tracer.HAND_RIGHT;
          }
          this.spawnTracer(sx, sy, sz, endX, endY, endZ, msg.weaponId);
        }
        // Wall impact spark + decal — only when we actually hit world geometry.
        if (hit.boxIndex >= 0) {
          this.spawnWallImpact(endX, endY, endZ, hit.normal, this.collisionBoxes[hit.boxIndex]);
        }
      }),
    );
  }

  /**
   * Approximate hand/gun anchor offsets from the eye, in meters. Matches
   * the typical "right-handed lower-third" gun-tip pose without depending
   * on a peer skeleton bone (which would also lag a tick behind the shot).
   * Small enough that any visual skew vs the true ray is imperceptible
   * past ~3 m, and the impact point still anchors the perceived line.
   */
  private static readonly HAND_FORWARD = 0.4;
  private static readonly HAND_RIGHT = 0.18;
  private static readonly HAND_DOWN = 0.22;
  /** Forward nudge from the resolved hand/grip to the muzzle tip (m), so the
   *  tracer starts at the barrel end rather than inside the receiver. */
  private static readonly MUZZLE_FORWARD = 0.45;

  destroy(): void {
    for (const u of this.unsubs) u();
    if (this.sparkTexture) this.sparkTexture.destroy();
    if (this.bulletHoleTexture) this.bulletHoleTexture.destroy();
  }

  /**
   * Ray vs all AABBs. Returns the smallest positive t (clamped at
   * MAX_RANGE) and the index of the hit box (-1 = no hit, t = MAX_RANGE).
   * Normal is the surface normal of the AABB face that was struck.
   */
  private rayWorldHit(
    ox: number, oy: number, oz: number,
    dx: number, dy: number, dz: number,
  ): { t: number; boxIndex: number; normal: pc.Vec3 } {
    let bestT = Tracer.MAX_RANGE;
    let bestIdx = -1;
    let bestNormal = new pc.Vec3(0, 1, 0);
    for (let i = 0; i < this.collisionBoxes.length; i++) {
      const b = this.collisionBoxes[i];
      // Slab method — find tEntry on each axis, take max.
      let tNear = -Infinity;
      let tFar = Infinity;
      // Axis encoded as 0=x, 1=y, 2=z so TS keeps the variable widened
      // through the closure without narrowing to a literal type.
      let nearAxisIdx = 0;
      let nearSign = 1;

      const slab = (oC: number, dC: number, lo: number, hi: number, axisIdx: number): boolean => {
        if (Math.abs(dC) < 1e-9) {
          if (oC < lo || oC > hi) return false;
          return true;
        }
        let t0 = (lo - oC) / dC;
        let t1 = (hi - oC) / dC;
        let sign = -1;
        if (t0 > t1) { const tmp = t0; t0 = t1; t1 = tmp; sign = 1; }
        if (t0 > tNear) { tNear = t0; nearAxisIdx = axisIdx; nearSign = sign; }
        if (t1 < tFar) tFar = t1;
        return tNear <= tFar;
      };

      if (!slab(ox, dx, b.minX, b.maxX, 0)) continue;
      if (!slab(oy, dy, b.minY, b.maxY, 1)) continue;
      if (!slab(oz, dz, b.minZ, b.maxZ, 2)) continue;
      if (tNear < 0 || tNear > tFar) continue;
      if (tNear >= bestT) continue;

      bestT = tNear;
      bestIdx = i;
      bestNormal = new pc.Vec3(
        nearAxisIdx === 0 ? nearSign : 0,
        nearAxisIdx === 1 ? nearSign : 0,
        nearAxisIdx === 2 ? nearSign : 0,
      );
    }
    return { t: bestT, boxIndex: bestIdx, normal: bestNormal };
  }

  private spawnTracer(
    ox: number, oy: number, oz: number,
    ex: number, ey: number, ez: number,
    weaponId: string,
  ): void {
    let spec;
    try {
      spec = getWeapon(weaponId);
    } catch {
      return;
    }
    // Tracer color + thickness vary by class for a quick read.
    // Thinner and brighter to look like a fast light streak, not a white rectangle.
    let thickness = 0.008;
    let color = new pc.Color(1.0, 0.7, 0.3);
    let emissiveIntensity = 8;
    switch (spec.class) {
      case "pistol":
        thickness = 0.005;
        color = new pc.Color(1.0, 0.9, 0.6);
        emissiveIntensity = 6;
        break;
      case "smg":
        thickness = 0.006;
        color = new pc.Color(1.0, 0.7, 0.3);
        emissiveIntensity = 7;
        break;
      case "rifle":
        thickness = 0.008;
        color = new pc.Color(1.0, 0.55, 0.18);
        emissiveIntensity = 8;
        break;
      case "sniper":
        thickness = 0.012;
        color = new pc.Color(1.0, 0.4, 0.12);
        emissiveIntensity = 10;
        break;
    }

    const dx = ex - ox, dy = ey - oy, dz = ez - oz;
    const length = Math.max(0.2, Math.hypot(dx, dy, dz));
    if (length < 0.1) return;

    const tracer = new pc.Entity("peer-tracer");
    tracer.addComponent("render", { type: "cylinder" });
    const mat = new pc.StandardMaterial();
    mat.diffuse = new pc.Color(0, 0, 0);
    mat.emissive = color;
    mat.emissiveIntensity = emissiveIntensity;
    mat.useLighting = false;
    mat.blendType = pc.BLEND_ADDITIVE;
    mat.depthWrite = false;
    mat.opacity = 0.9;
    mat.update();
    if (tracer.render) {
      tracer.render.material = mat;
      tracer.render.castShadows = false;
      tracer.render.receiveShadows = false;
    }
    // Cylinder native height = 1 (Y axis); scale Y to length and orient
    // by pointing tracer.up at the end point.
    tracer.setLocalScale(thickness, length, thickness);
    const midX = (ox + ex) * 0.5;
    const midY = (oy + ey) * 0.5;
    const midZ = (oz + ez) * 0.5;
    tracer.setPosition(midX, midY, midZ);
    // Align cylinder Y-axis with the direction vector. PlayCanvas helper
    // setLocalEulerAngles won't work cleanly with arbitrary axes; use
    // lookAt then pitch +90° (same trick as LocalWeaponView.spawnTracer).
    const endVec = new pc.Vec3(ex, ey, ez);
    tracer.lookAt(endVec);
    tracer.rotateLocal(90, 0, 0);

    this.app.root.addChild(tracer);

    const DURATION = 0.12;
    let age = 0;
    const onTick = (dt: number) => {
      age += dt;
      if (age >= DURATION) {
        this.app.off("update", onTick);
        tracer.destroy();
        return;
      }
      const t = age / DURATION;
      mat.opacity = 0.9 * (1 - t);
      mat.emissiveIntensity = emissiveIntensity * (1 - t);
      mat.update();
    };
    this.app.on("update", onTick);
  }

  private spawnWallImpact(
    x: number, y: number, z: number,
    normal: pc.Vec3,
    box: AABB,
  ): void {
    // 1. Spark Particle System (Fast, subtle burst)
    const sparks = new pc.Entity("wall-sparks");
    sparks.setPosition(x + normal.x * 0.02, y + normal.y * 0.02, z + normal.z * 0.02);
    
    // Tangent basis in the impact plane for spread
    const refX = Math.abs(normal.x) < 0.9 ? 1 : 0;
    const refY = Math.abs(normal.x) < 0.9 ? 0 : 1;
    const refZ = 0;
    const t1x = normal.y * refZ - normal.z * refY;
    const t1y = normal.z * refX - normal.x * refZ;
    const t1z = normal.x * refY - normal.y * refX;
    const t1Mag = Math.hypot(t1x, t1y, t1z) || 1;
    const u1 = new pc.Vec3(t1x / t1Mag, t1y / t1Mag, t1z / t1Mag);
    const u2 = new pc.Vec3(
      normal.y * u1.z - normal.z * u1.y,
      normal.z * u1.x - normal.x * u1.z,
      normal.x * u1.y - normal.y * u1.x,
    );

    const outMag = 3.0; // Speed bouncing off the wall
    const spread = 2.0; // Spread along the wall
    const gravity = 8.0;
    const sparkLife = 0.25; // Very fast, doesn't obscure vision

    const velocityGraph = new pc.CurveSet([
      [0, normal.x * outMag - spread, 1, normal.x * outMag - spread],
      [0, normal.y * outMag - spread, 1, normal.y * outMag - spread - gravity * sparkLife],
      [0, normal.z * outMag - spread, 1, normal.z * outMag - spread]
    ]);
    const velocityGraph2 = new pc.CurveSet([
      [0, normal.x * outMag + spread, 1, normal.x * outMag + spread],
      [0, normal.y * outMag + spread, 1, normal.y * outMag + spread - gravity * sparkLife],
      [0, normal.z * outMag + spread, 1, normal.z * outMag + spread]
    ]);

    // Small, fast-fading sparks
    const scaleGraph = new pc.Curve([0, 0.02, 1, 0.005]);
    const alphaGraph = new pc.Curve([0, 1, 0.5, 1, 1, 0]);

    sparks.addComponent("particlesystem", {
      numParticles: 6, // Low count so it's not noisy
      lifetime: sparkLife,
      rate: 0, // Burst
      colorMap: this.sparkTexture,
      emitterShape: pc.EMITTERSHAPE_SPHERE,
      emitterRadius: 0.02,
      velocityGraph: velocityGraph,
      velocityGraph2: velocityGraph2,
      scaleGraph: scaleGraph,
      alphaGraph: alphaGraph,
      blendType: pc.BLEND_ADDITIVE,
      loop: false,
      autoPlay: true,
      depthWrite: false,
      sort: pc.SORTMODE_BACK2FRONT
    });
    this.app.root.addChild(sparks);

    // 2. Textured Bullet Hole Decal
    const decal = new pc.Entity("wall-decal");
    decal.addComponent("render", { type: "plane" });
    // Offset slightly more to prevent z-fighting
    decal.setPosition(x + normal.x * 0.005, y + normal.y * 0.005, z + normal.z * 0.005);
    
    // Orient plane to face OUT from the wall normal
    const target = new pc.Vec3(x + normal.x, y + normal.y, z + normal.z);
    decal.lookAt(target);
    // Plane native orientation faces +Y, so we pitch it 90 degrees to face +Z (forward)
    decal.rotateLocal(90, 0, 0);
    
    // Small bullet hole size
    decal.setLocalScale(0.06, 0.06, 0.06);
    
    const decalMat = new pc.StandardMaterial();
    decalMat.diffuse = new pc.Color(1, 1, 1); // Let texture dictate color
    decalMat.diffuseMap = this.bulletHoleTexture;
    decalMat.useLighting = true;
    decalMat.blendType = pc.BLEND_NORMAL;
    decalMat.opacityMap = this.bulletHoleTexture;
    decalMat.opacityMapChannel = "a";
    decalMat.depthWrite = false;
    // Add a tiny bit of random rotation so bullet holes don't all look identical
    decal.rotateLocal(0, Math.random() * 360, 0);
    
    decalMat.update();
    if (decal.render) {
      decal.render.material = decalMat;
      decal.render.castShadows = false;
      decal.render.receiveShadows = false;
    }
    this.app.root.addChild(decal);

    const DECAL_LIFE = 8.0;
    let age = 0;
    const onTick = (dt: number) => {
      age += dt;
      
      // Cleanup sparks after they finish
      if (age > sparkLife && sparks.enabled) {
        sparks.enabled = false;
        sparks.destroy();
      }

      // Decal fade out at the end of its life
      const dT = age / DECAL_LIFE;
      if (dT < 1) {
        // Only start fading in the last 20% of its life
        if (dT > 0.8) {
          const fade = 1 - ((dT - 0.8) * 5); // map 0.8-1.0 to 1.0-0.0
          decalMat.opacity = fade;
          decalMat.update();
        }
      } else {
        this.app.off("update", onTick);
        decal.destroy();
      }
    };
    this.app.on("update", onTick);
  }
}
